import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Question, Survey } from '../../../../types/domainModels';
import { stripHtml } from '../../../../util/richText';

import { HeaderWithEdit } from '../HeaderWithEdit/HeaderWithEdit';

export interface SurveyQuota {
  definitions: ReactNode[];
  questionTitle: string;
}

function getSurveyQuotasFromQuestions(questions: Question[]): SurveyQuota[] {
  const surveyQuotas: SurveyQuota[] = [];

  const questionsWithQuotas = questions.filter(({ questionQuotas }) => {
    return !!(questionQuotas && questionQuotas.length > 0);
  });

  questionsWithQuotas.forEach(
    ({ options, questionQuotas = [], sort, title }, i) => {
      surveyQuotas.push({
        definitions: questionQuotas.map(
          ({ logicalModifier, numberNeeded, questionOptions }) => {
            let modifierLabel = '';
            if (logicalModifier === 'all') {
              modifierLabel = 'All';
            } else if (logicalModifier === 'none') {
              modifierLabel = 'No';
            } else if (logicalModifier === 'at_most') {
              modifierLabel = `At most ${numberNeeded}`;
            } else if (logicalModifier === 'at_least') {
              modifierLabel = `At least ${numberNeeded}`;
            }

            return (
              <span key={i}>
                <strong className="font-bold">{modifierLabel}</strong> responses
                for{' '}
                {questionOptions
                  .map(({ id }) => {
                    return options.find((o) => o.id === id)?.title;
                  })
                  .join(' or ')}
              </span>
            );
          },
        ),
        questionTitle: `Q${sort}) ${stripHtml(title)}`,
      });
    },
  );

  return surveyQuotas;
}

export const Quotas = ({
  questions,
  survey,
}: {
  questions: Question[];
  survey: Survey;
}): JSX.Element => {
  const navigate = useNavigate();

  const surveyQuotas = getSurveyQuotasFromQuestions(questions);

  return (
    <div className="space-y-2">
      <HeaderWithEdit
        onClickEdit={() => {
          navigate(`/surveys/${survey.id}/build/audience`);
        }}
        title="Quotas"
      />
      {surveyQuotas.length === 0 && <p>No quotas</p>}
      {surveyQuotas.map(({ definitions, questionTitle }, i) => {
        return (
          <div key={i}>
            <p className="italic">{stripHtml(questionTitle)}</p>
            <ul className="list-disc pl-8">
              {definitions.map((definition, i) => {
                return <li key={i}>{definition}</li>;
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
